/**
 * Data hook for the Cases Recycle Bin
 * (`cases-recycle-bin-lexide-handoff-2026-07-19.md`).
 *
 * Deliberately NOT fail-soft in the way a decorative pane would be. This is
 * a RECOVERY surface: if the listing cannot be fetched, the user must be
 * told plainly, because the alternative — an empty bin — reads as "your
 * deleted case is gone", which is both false and alarming. So an error is
 * surfaced as an error and never as an empty list.
 *
 * Paged because the bin accumulates: a firm retiring throwaway cases for
 * months would otherwise pull everything on every open. The server clamps
 * `limit` (default 100, max 500) and reads case models for the requested
 * PAGE only, so depth costs nothing until asked for.
 */
import { useCallback, useEffect, useRef, useState } from "react";

import { useGatewayStore } from "@/stores/useGatewayStore";
import {
  listRecycleBin,
  RECYCLE_BIN_PAGE_SIZE,
  type RecycleBinEntry,
} from "@/api/lx-recycle-bin";

export interface UseRecycleBinResult {
  /** True while the socket is down/reconnecting. The pane shows a WAITING
   *  state for this, not an error: a reconnect is transient and self-heals,
   *  and calling it a failure teaches the lawyer to distrust a recovery
   *  surface that was merely mid-handshake. */
  isDisconnected: boolean;
  entries: RecycleBinEntry[];
  total: number;
  hasMore: boolean;
  isLoading: boolean;
  /** Set when the listing itself failed. Never accompanied by a silent
   *  empty list — see the header. */
  error: string | null;
  refetch: () => Promise<void>;
  loadMore: () => Promise<void>;
}

export function useRecycleBin(enabled = true): UseRecycleBinResult {
  const [entries, setEntries] = useState<RecycleBinEntry[]>([]);
  const [total, setTotal] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [isLoading, setIsLoading] = useState(enabled);
  const [error, setError] = useState<string | null>(null);
  const disposedRef = useRef(false);
  // The gateway's own connection state — the bin must FOLLOW it rather than
  // freeze on whatever the socket happened to be doing at mount. Before this,
  // a fetch attempted mid-reconnect stored "Gateway not connected" and the
  // pane sat on that stale message indefinitely: no retry when the socket
  // came back, so a self-healing condition needed a manual click to clear.
  const connectionState = useGatewayStore((st) => st.connectionState);
  const isConnected = connectionState === "connected";

  useEffect(() => {
    disposedRef.current = false;
    return () => {
      disposedRef.current = true;
    };
  }, []);

  const fetchPage = useCallback(
    async (offset: number, append: boolean) => {
      setIsLoading(true);
      try {
        const page = await listRecycleBin({
          limit: RECYCLE_BIN_PAGE_SIZE,
          offset,
        });
        if (disposedRef.current) return;
        setEntries((prev) =>
          append ? [...prev, ...(page.items ?? [])] : (page.items ?? []),
        );
        setTotal(page.total ?? 0);
        setHasMore(Boolean(page.hasMore));
        setError(null);
      } catch (err) {
        if (disposedRef.current) return;
        // Surfaced, not swallowed: an empty bin and an unreachable bin must
        // never look the same to someone trying to recover a case.
        setError(err instanceof Error ? err.message : "recycle bin unavailable");
      } finally {
        if (!disposedRef.current) setIsLoading(false);
      }
    },
    [],
  );

  useEffect(() => {
    if (!enabled) return;
    if (!isConnected) return; // wait for the socket; do not record an error
    // Re-runs when `isConnected` flips false -> true, so recovery is
    // automatic. `fetchPage` is stable, so this cannot loop.
    void fetchPage(0, false);
  }, [enabled, isConnected, fetchPage]);

  // A transient disconnect must not leave a stale error behind once the
  // socket returns — the reconnect IS the resolution.
  useEffect(() => {
    if (isConnected) setError(null);
  }, [isConnected]);

  const refetch = useCallback(() => fetchPage(0, false), [fetchPage]);
  // Offset paging over a list that can mutate underneath us: if an entry is
  // restored or purged between pages, the server list shrinks and this offset
  // can SKIP a row (it cannot duplicate one, and the button is disabled while
  // loading, so there is no double-append race). A refetch re-reads from 0 and
  // corrects it. Cursor paging would remove the skip, but the contract is
  // limit/offset and a stale-by-one bin heals on the next refresh.
  const loadMore = useCallback(
    () => fetchPage(entries.length, true),
    [fetchPage, entries.length],
  );

  return {
    entries,
    total,
    hasMore,
    // Show the spinner while waiting on the socket too, so the surface never
    // renders "empty bin" during a reconnect.
    isLoading: isLoading || (enabled && !isConnected && entries.length === 0),
    isDisconnected: enabled && !isConnected,
    error,
    refetch,
    loadMore,
  };
}
